import { type RefObject, useCallback, useRef } from "react";

import { useMountEffect } from "@/web/lib/effects";

/**
 * Viewport width (px) at or below which the story uses its mobile geometry.
 * Must agree with the `max-[760px]:` Tailwind variants in TransitionStory and
 * the `max-width: 760px` rules in home.css.
 */
export const STORY_MOBILE_MAX_PX = 760;

/**
 * Story progress each stage button scrolls to ("01 A building", …). Stage 01
 * sits just past the card's appear/zoom (done at 0.29) so it lands settled.
 */
const STAGE_PROGRESS = [0.3, 0.43, 0.65] as const;

/** The SVG's box inside the finished card, as fractions of the card's height. */
const SVG_BOX = { desktop: { top: 0.18, height: 0.58 }, mobile: { top: 0.22, height: 0.52 } };
/** The persistent building's vertical extent and center, in facade user units. */
const BUILDING = { top: 151, bottom: 936, centerX: 737 };
/**
 * Where the building ends up in the finished card, as fractions of its height:
 * below the title/address (which end at ~0.27) and above `$100/share` (which
 * starts at 0.75). Both are fixed fractions because `.story-card` scales its
 * contents with the card's height.
 */
const BUILDING_BAND = { top: 0.32, bottom: 0.7 };

/**
 * The viewBox the zoom ends on: the one that maps the building onto
 * `BUILDING_BAND`. It must stay taller than the SVG box's aspect so `meet` fits
 * by height (the mapping assumes it); the story card is always far wider.
 */
function finalViewBox(box: { top: number; height: number }): number[] {
  const height =
    (box.height * (BUILDING.bottom - BUILDING.top)) / (BUILDING_BAND.bottom - BUILDING_BAND.top);
  const width = height * (490 / 940);
  const y = BUILDING.top - ((BUILDING_BAND.top - box.top) * height) / box.height;
  return [BUILDING.centerX - width / 2, y, width, height];
}

const clamp = (n: number) => Math.max(0, Math.min(1, n));
const range = (n: number, a: number, b: number) => clamp((n - a) / (b - a));
const mix = (a: number, b: number, t: number) => a + (b - a) * t;

/** Every DOM node the story drives. Created by `useTransitionStoryRefs`. */
export type TransitionStoryRefs = {
  story: RefObject<HTMLElement | null>;
  sticky: RefObject<HTMLDivElement | null>;
  canvas: RefObject<HTMLDivElement | null>;
  frame: RefObject<HTMLDivElement | null>;
  buildBusiness: RefObject<HTMLDivElement | null>;
  buildTags: RefObject<HTMLDivElement | null>;
  buildTitle: RefObject<HTMLElement | null>;
  buildAddress: RefObject<HTMLParagraphElement | null>;
  buildShares: RefObject<HTMLDivElement | null>;
  buildBackers: RefObject<HTMLDivElement | null>;
  svg: RefObject<SVGSVGElement | null>;
  photo: RefObject<SVGImageElement | null>;
  ink: RefObject<SVGGElement | null>;
  persistent: RefObject<SVGGElement | null>;
  neighbors: RefObject<SVGGElement | null>;
  business: RefObject<SVGGElement | null>;
  shared: RefObject<SVGGElement | null>;
  community: RefObject<SVGGElement | null>;
  copy: RefObject<HTMLDivElement | null>;
  copies: RefObject<(HTMLDivElement | null)[]>;
  caption: RefObject<HTMLDivElement | null>;
  stages: RefObject<HTMLElement | null>;
  tabs: RefObject<(HTMLButtonElement | null)[]>;
  progress: RefObject<HTMLSpanElement | null>;
  /** The portal-owned fixed layer the handoff clone flies through. */
  layer: RefObject<HTMLDivElement | null>;
};

export function useTransitionStoryRefs(): TransitionStoryRefs {
  return {
    story: useRef(null),
    sticky: useRef(null),
    canvas: useRef(null),
    frame: useRef(null),
    buildBusiness: useRef(null),
    buildTags: useRef(null),
    buildTitle: useRef(null),
    buildAddress: useRef(null),
    buildShares: useRef(null),
    buildBackers: useRef(null),
    svg: useRef(null),
    photo: useRef(null),
    ink: useRef(null),
    persistent: useRef(null),
    neighbors: useRef(null),
    business: useRef(null),
    shared: useRef(null),
    community: useRef(null),
    copy: useRef(null),
    copies: useRef([]),
    caption: useRef(null),
    stages: useRef(null),
    tabs: useRef([]),
    progress: useRef(null),
    layer: useRef(null),
  };
}

type StoryNodes = {
  [K in keyof TransitionStoryRefs]: NonNullable<TransitionStoryRefs[K]["current"]>;
};

function resolve(refs: TransitionStoryRefs): StoryNodes | null {
  const nodes = {} as Record<string, unknown>;
  for (const [key, ref] of Object.entries(refs)) {
    // The handoff layer mounts client-only (portal); its absence just skips the handoff.
    if (ref.current == null && key !== "layer") return null;
    nodes[key] = ref.current;
  }
  return nodes as StoryNodes;
}

function setStyle(el: HTMLElement | SVGElement, style: Partial<Record<string, string>>) {
  Object.assign(el.style, style);
}

/**
 * Drives the homepage scroll story (port of the prototype's `hybrid.js`):
 * the facade photo dissolves into line art, zooms into an offering card, and —
 * past the end of the sticky scroll — the card flies into the first slot of the
 * featured-offerings rail, cross-dissolving into the real first card.
 *
 * `railAnchor` is an always-rendered element at the rail's position (used to
 * time the handoff). The returned `setHandoffTarget` is a callback ref for the
 * first real card; until it's attached (cards loading, zero results, error) the
 * handoff is skipped and the story card simply fades out.
 */
export function useTransitionStory(
  refs: TransitionStoryRefs,
  railAnchor: RefObject<HTMLElement | null>,
) {
  const handoffTarget = useRef<HTMLElement | null>(null);
  const scheduleRef = useRef<() => void>(() => {});
  const reducedRef = useRef(false);

  // Per-frame values are written straight to the DOM inside a rAF-throttled
  // scroll/resize listener, exactly as the prototype does. Pushing them through
  // React state would re-render the whole story ~60×/s. This is the one
  // justified side effect on the page, hence a mount effect.
  useMountEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = reduce.matches;
    let pending = 0;
    let active = -1;

    function updateHandoff(n: StoryNodes) {
      const target = handoffTarget.current;
      const layer = n.layer;
      const rail = railAnchor.current;
      if (reduce.matches) {
        if (layer) layer.hidden = true;
        if (target) target.style.opacity = "1";
        return;
      }
      const storyTop = window.scrollY + n.story.getBoundingClientRect().top;
      const start = storyTop + 0.9 * (n.story.offsetHeight - n.sticky.offsetHeight);
      const end = rail
        ? window.scrollY + rail.getBoundingClientRect().top - window.innerHeight * 0.22
        : start + window.innerHeight;
      const t = clamp((window.scrollY - start) / Math.max(1, end - start));

      if (!target || !layer) {
        // No real card to land on: the illustrative card just fades out.
        if (layer) {
          layer.hidden = true;
          layer.replaceChildren();
        }
        n.canvas.style.opacity = String(1 - range(t, 0, 0.5));
        return;
      }

      const ease = t * t * (3 - 2 * t);
      const fade = range(t, 0.82, 1);
      layer.hidden = t <= 0 || t >= 1;
      target.style.opacity = t <= 0 ? "0" : String(fade);
      if (t <= 0 || t >= 1) {
        layer.replaceChildren();
        if (t >= 1) n.canvas.style.opacity = "0";
        return;
      }
      // Refresh from the live card so resizing and reverse scrolling stay exact.
      // The clone is resized by layout, not a transform: the story frame and the
      // rail card have different aspect ratios, so a scale(sx, sy) would squash
      // the card's contents. Sized for real, the frame's container-relative
      // geometry (home.css `.story-card`) reflows, and lands on the real card's
      // exact pixels once the frame matches the destination.
      const destination = target.getBoundingClientRect();
      const carried = n.canvas.cloneNode(true) as HTMLDivElement;
      const source = n.frame.getBoundingClientRect();
      const sourceTop = source.top - n.sticky.getBoundingClientRect().top;
      // Canvas px per frame px, constant as both scale (the frame's insets are %).
      const kx = n.canvas.offsetWidth / n.frame.offsetWidth;
      const ky = n.canvas.offsetHeight / n.frame.offsetHeight;
      const frameWidth = mix(source.width, destination.width, ease);
      const frameHeight = mix(source.height, destination.height, ease);
      setStyle(carried, {
        position: "absolute",
        width: `${frameWidth * kx}px`,
        height: `${frameHeight * ky}px`,
        left: `${mix(source.left, destination.left, ease) - n.frame.offsetLeft * (frameWidth / n.frame.offsetWidth)}px`,
        top: `${mix(sourceTop, destination.top, ease) - n.frame.offsetTop * (frameHeight / n.frame.offsetHeight)}px`,
        transform: "none",
        opacity: String(1 - fade),
      });
      // The frame is the canvas's first child (see TransitionStory).
      const carriedFrame = carried.firstElementChild;
      if (carriedFrame instanceof HTMLElement) carriedFrame.style.background = "#fff";
      layer.replaceChildren(carried);
      n.canvas.style.opacity = "0";
    }

    function render() {
      pending = 0;
      const n = resolve(refs);
      if (!n) return;
      const p = clamp(
        -n.story.getBoundingClientRect().top / (n.story.offsetHeight - window.innerHeight),
      );
      n.progress.style.transform = `scaleX(${p})`;

      if (reduce.matches) {
        // Static, fully drawn illustration with no photo; layout comes from home.css.
        for (const el of [
          n.buildBusiness,
          n.buildAddress,
          n.buildTitle,
          n.buildTags,
          n.buildShares,
          n.buildBackers,
          n.frame,
        ]) {
          setStyle(el, { opacity: "1", transform: "none" });
        }
        setStyle(n.canvas, { transform: "none", opacity: "1" });
        setStyle(n.svg, { top: "", height: "", clipPath: "" });
        n.photo.style.opacity = "0";
        n.ink.style.opacity = "1";
        for (const path of n.ink.getElementsByTagName("path")) path.style.strokeDashoffset = "0";
        n.svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        n.svg.setAttribute("viewBox", "0 0 1920 1100");
        for (const g of [n.neighbors, n.business, n.shared, n.community]) g.style.opacity = "1";
        for (const el of n.copies) {
          if (!el) continue;
          el.setAttribute("aria-hidden", "false");
          el.inert = false;
        }
        updateHandoff(n);
        return;
      }

      const fade = range(p, 0.07, 0.18);
      const appear = range(p, 0.2, 0.29);
      const zoom = range(p, 0.18, 0.29);
      const mobile = window.innerWidth <= STORY_MOBILE_MAX_PX;

      n.photo.style.opacity = String(1 - fade);
      n.ink.style.opacity = String(range(p, 0.01, 0.06));
      const drawn = range(p, 0.01, 0.1);
      for (const path of n.persistent.children) {
        if (!(path instanceof SVGElement)) continue;
        path.style.strokeDashoffset = String(1 - drawn);
        path.style.strokeDasharray = drawn >= 1 ? "none" : "1";
      }

      // Crop the photo and its traced geometry together to fill the initial
      // viewport. Matching the starting viewBox ratio avoids SVG's default side
      // letterboxing.
      const ratio = n.sticky.clientWidth / Math.max(1, n.sticky.clientHeight);
      const cropWidth = Math.min(1920, 1280 * ratio);
      const cropHeight = Math.min(1280, 1920 / ratio);
      const cropX = Math.max(0, Math.min(1920 - cropWidth, 737 - cropWidth / 2));
      const box = mobile ? SVG_BOX.mobile : SVG_BOX.desktop;
      // Photo and line art share one user space, so only the zoom's end moves;
      // the drawing stays registered to the photo throughout.
      const target = finalViewBox(box);
      const full = [cropX, (1280 - cropHeight) / 2, cropWidth, cropHeight];
      n.svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      n.svg.setAttribute("viewBox", full.map((v, i) => mix(v, target[i] ?? v, zoom)).join(" "));

      setStyle(n.canvas, {
        width: `${mobile ? 100 : mix(100, 60, zoom)}%`,
        height: `${mobile ? mix(100, 41, zoom) : mix(100, 76, zoom)}%`,
        top: `${mix(0, mobile ? 11 : 12, zoom)}%`,
        // Keep the actual primary card alive as it shrinks into the first grid cell.
        transform: "none",
        opacity: "1",
      });
      n.canvas.style.setProperty("--building-stroke", "1.1");
      n.canvas.style.setProperty("--card-stage", String(appear));
      setStyle(n.svg, {
        clipPath: `inset(0 ${appear * (mobile ? 15 : 9)}%)`,
        top: `${appear * box.top * 100}%`,
        height: `${100 - appear * (100 - box.height * 100)}%`,
      });

      n.copy.style.opacity = String(appear);
      n.neighbors.style.opacity = "0";
      n.business.style.opacity = String(range(p, 0.38, 0.42));
      n.shared.style.opacity = String(range(p, 0.57, 0.62));
      n.community.style.opacity = "0";

      n.frame.style.opacity = String(appear);
      n.buildBusiness.style.opacity = "1";
      n.buildAddress.style.opacity = String(appear);
      const businessReveal = range(p, 0.38, 0.42);
      const fundingReveal = range(p, 0.57, 0.62);
      n.buildTitle.style.opacity = String(businessReveal);
      // Collapse unrevealed rows so each new piece pushes the existing content down.
      const tagsSpace =
        n.buildTags.offsetHeight + Number.parseFloat(getComputedStyle(n.buildTitle).marginTop);
      const titleSpace =
        n.buildTitle.offsetHeight + Number.parseFloat(getComputedStyle(n.buildAddress).marginTop);
      n.buildTitle.style.transform = `translateY(${-(1 - fundingReveal) * tagsSpace}px)`;
      n.buildAddress.style.transform = `translateY(${
        -(1 - fundingReveal) * tagsSpace - (1 - businessReveal) * titleSpace
      }px)`;
      for (const el of [n.buildTags, n.buildShares, n.buildBackers]) {
        setStyle(el, {
          opacity: String(fundingReveal),
          transform: `translateY(${(1 - fundingReveal) * 16}px)`,
        });
      }

      n.caption.style.opacity = String(1 - range(p, 0, 0.06));
      n.stages.style.opacity = String(appear);
      n.stages.style.pointerEvents = appear > 0.9 ? "auto" : "none";
      for (const tab of n.tabs) if (tab) tab.tabIndex = appear > 0.9 ? 0 : -1;

      const idx = p < 0.38 ? 0 : p < 0.57 ? 1 : 2;
      if (idx !== active) {
        active = idx;
        n.copies.forEach((el, i) => {
          if (!el) return;
          el.classList.toggle("active", i === idx);
          el.setAttribute("aria-hidden", String(i !== idx));
          el.inert = i !== idx;
        });
        n.tabs.forEach((el, i) => {
          if (!el) return;
          if (i === idx) el.setAttribute("aria-current", "step");
          else el.removeAttribute("aria-current");
        });
      }
      updateHandoff(n);
    }

    function schedule() {
      if (!pending) pending = requestAnimationFrame(render);
    }
    const onMotionChange = () => {
      reducedRef.current = reduce.matches;
      active = -1;
      schedule();
    };

    scheduleRef.current = schedule;
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    reduce.addEventListener("change", onMotionChange);
    render();

    return () => {
      scheduleRef.current = () => {};
      if (pending) cancelAnimationFrame(pending);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      reduce.removeEventListener("change", onMotionChange);
      refs.layer.current?.replaceChildren();
    };
  });

  /** Callback ref for the first real rail card — the handoff's destination. */
  const setHandoffTarget = useCallback((el: HTMLElement | null) => {
    handoffTarget.current = el;
    scheduleRef.current();
  }, []);

  /** Re-time the handoff when the rail scrolls horizontally. */
  const onRailScroll = useCallback(() => scheduleRef.current(), []);

  /** Scroll to the story progress point for stage `i`. */
  const jumpToStage = useCallback(
    (i: number) => {
      const story = refs.story.current;
      if (!story) return;
      const at = STAGE_PROGRESS[i] ?? 0;
      window.scrollTo({
        top:
          window.scrollY +
          story.getBoundingClientRect().top +
          at * (story.offsetHeight - window.innerHeight),
        behavior: reducedRef.current ? "instant" : "smooth",
      });
    },
    [refs.story],
  );

  return { setHandoffTarget, onRailScroll, jumpToStage };
}
